/**
 * UpdateBanner — shown when a new service worker version is waiting to activate.
 *
 * Listens for the custom 'amal:update-available' event dispatched from
 * src/index.js (which itself listens to workbox-window's 'waiting' event when
 * a fresh service worker is installed but blocked by the current one still
 * controlling the page).
 *
 * User clicks "Reload to update" → we send SKIP_WAITING to the SW → the new
 * SW takes over → we reload the page. User gets the new version on next paint.
 *
 * Pure addition, mounted near the top of App. Renders nothing if no update.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiDownload, FiX } from 'react-icons/fi';

const UpdateBanner = () => {
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    const onUpdate = () => setShowUpdate(true);
    window.addEventListener('amal:update-available', onUpdate);
    return () => window.removeEventListener('amal:update-available', onUpdate);
  }, []);

  const applyUpdate = async () => {
    // workbox-window exposes itself on window.__AMAL_WB__ (set in src/index.js).
    // It posts SKIP_WAITING to the waiting SW; on activation the new SW takes
    // over and we reload so the page is served by the new version.
    const wb = window.__AMAL_WB__;
    if (!wb) {
      // Fallback: just reload — the new SW will activate on next navigation
      window.location.reload();
      return;
    }
    wb.addEventListener('controlling', () => {
      window.location.reload();
    });
    wb.messageSkipWaiting();
  };

  if (!showUpdate) return null;

  return (
    <Bar role="status" aria-live="polite">
      <Inner>
        <FiDownload size={16} />
        <Text>A new version of Amal ERP is ready.</Text>
        <ReloadBtn onClick={applyUpdate}>Reload now</ReloadBtn>
        <DismissBtn onClick={() => setShowUpdate(false)} aria-label="Dismiss">
          <FiX size={14} />
        </DismissBtn>
      </Inner>
    </Bar>
  );
};

export default UpdateBanner;

// ───────────── styles ─────────────
const Bar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #800020;
  color: #fff;
  z-index: 10000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
`;

const Text = styled.span`
  flex: 1;
`;

const ReloadBtn = styled.button`
  background: #fff;
  color: #800020;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const DismissBtn = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { background: rgba(255, 255, 255, 0.1); }
`;
